import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import { BOOK_TITLE, BOOK_AUTHOR, BOOK_AMAZON_URL, fetchExcerpt, chapterNameFromTitle } from '../utils/book';
import './GeniusReader.css';

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

// What the mark means, by how the server found it (functions/book.js selectExcerpt): the note
// under the text, and what a screen reader hears before the marked words.
const ANCHOR_NOTES = {
  quote: { note: 'Marked: the words AI Rob quoted.', spoken: 'The words AI Rob quoted, marked: ' },
  para: { note: 'Marked: the passage AI Rob read.', spoken: 'The passage AI Rob read: ' },
  match: { note: 'Marked: the part that matches AI Rob’s words.', spoken: 'The part that matches AI Rob’s words: ' },
};

/**
 * The Genius reader: the passage of Pastor Rob's book an answer cited, with
 * the part it drew on marked, a few paragraphs either side (functions/book.js
 * chooses them; the whole book never leaves the server). Rob's written words
 * are set in Newsreader; everything around them is Inter. What is marked, and
 * the note under the text that says so, follow how the server found it
 * (anchor): the words AI Rob quoted, word for word ("quote", only those words
 * marked); the passage AI Rob read ("para"); or the part that matches AI
 * Rob's words ("match").
 *
 * A real dialog: focus moves in to the close button, Tab stays inside, Escape
 * and the close button and the scrim close it, and focus goes back to what
 * opened it (ChatView). A sheet from the bottom on phones, a centred panel
 * wider up (the shared .apr-overlay / .apr-dialog).
 *
 *   chapter       the number the citation named (0: the Introduction)
 *   quote         the words the server verified in the book, or the answer's
 *                 words just before the citation
 *   para          where the server said the answer read the chapter, if it did
 *   fallbackTitle the citation's own name, for before the text arrives
 *   onClose()     close; ChatView returns focus to the opener
 *   onAsk(title)  "Ask about this passage": close and fill the box, not send
 */
function GeniusReader({ chapter, quote, para, fallbackTitle = '', onClose, onAsk }) {
  const [state, setState] = useState({ phase: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const textRef = useRef(null);
  const markRef = useRef(null);

  useEffect(() => {
    let live = true;
    setState({ phase: 'loading' });
    fetchExcerpt({ chapter, quote, para })
      .then((data) => {
        if (!live) return;
        setState(data && data.available && Array.isArray(data.paragraphs) && data.paragraphs.length
          ? { phase: 'ready', data }
          : { phase: 'missing' });
      })
      .catch((err) => {
        if (live) setState({ phase: 'error', message: err && err.safeMessage });
      });
    return () => { live = false; };
  }, [chapter, quote, para, attempt]);

  useEffect(() => {
    const t = setTimeout(() => closeRef.current?.focus({ preventScroll: true }), 30);
    return () => clearTimeout(t);
  }, []);

  // The marked paragraph sits near the top of the text, its lead-in above it.
  useEffect(() => {
    if (state.phase !== 'ready') return;
    const box = textRef.current;
    const mark = markRef.current;
    if (box && mark) box.scrollTop = Math.max(0, mark.offsetTop - box.offsetTop - 48);
  }, [state]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const items = [...dialogRef.current.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onKeyDown]);

  const data = state.phase === 'ready' ? state.data : null;
  const chapterLabel = data
    ? [data.label, data.title].filter(Boolean).join(' · ')
    : String(fallbackTitle || '').replace(/^Genius\s*·\s*/, '').replace(/:\s*/, ' · ');
  const askTitle = data ? (data.title || data.label) : chapterNameFromTitle(fallbackTitle);
  const matched = data && data.highlight != null;
  // How the server found it; an older server says only where (highlight).
  const anchor = data && (ANCHOR_NOTES[data.anchor] ? data.anchor : matched ? 'match' : null);
  const marksIn = (i) => (data && Array.isArray(data.marks) ? data.marks.filter((m) => m.index === i) : []);

  return createPortal(
    <div className="apr-overlay genius-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={dialogRef}
        className="apr-dialog genius-reader"
        role="dialog"
        aria-modal="true"
        aria-labelledby="genius-reader-title"
        aria-describedby="genius-reader-chapter"
      >
        <span className="apr-dialog-grabber" aria-hidden="true" />
        <button ref={closeRef} type="button" className="apr-icon-btn apr-icon-btn--bare apr-dialog-close" onClick={onClose} aria-label="Close">
          <Icon name="x" size={20} />
        </button>

        <header className="genius-head">
          <h2 id="genius-reader-title" className="genius-book">{BOOK_TITLE}</h2>
          <p id="genius-reader-chapter" className="genius-chapter">{chapterLabel}</p>
          <p className="genius-by">by {BOOK_AUTHOR}</p>
        </header>

        {state.phase === 'loading' && (
          <p className="genius-status" role="status">Opening the passage…</p>
        )}
        {state.phase === 'missing' && (
          <p className="genius-status" role="status">This passage isn’t available here yet.</p>
        )}
        {state.phase === 'error' && (
          <div className="genius-status" role="alert">
            <p className="apr-error"><Icon name="info" size={16} /><span>{state.message || 'This passage didn’t load. Please try again.'}</span></p>
            <button type="button" className="apr-btn apr-btn--text" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
          </div>
        )}
        {data && (
          <>
            {/* Scrolls by keyboard too, so it can take focus. */}
            <div ref={textRef} className="genius-text" tabIndex={0} role="region" aria-label="Passage from Genius">
              {data.paragraphs.map((p, i) => {
                if (p.heading) return <h3 key={i} className="genius-h">{p.text}</h3>;
                const marks = anchor === 'quote' ? marksIn(i) : [];
                if (marks.length) {
                  // Only the words AI Rob quoted are marked; the rest of the paragraph reads as it is.
                  const parts = [];
                  let at = 0;
                  marks.forEach((m, k) => {
                    if (m.start > at) parts.push(p.text.slice(at, m.start));
                    parts.push(<mark key={k} className="genius-mark is-quote">{p.text.slice(m.start, m.end)}</mark>);
                    at = m.end;
                  });
                  if (at < p.text.length) parts.push(p.text.slice(at));
                  return (
                    <p key={i} ref={i === data.highlight ? markRef : undefined} className="genius-p is-cited">
                      <span className="apr-sr-only">{ANCHOR_NOTES.quote.spoken}</span>
                      {parts}
                    </p>
                  );
                }
                if (i === data.highlight) {
                  return (
                    <p key={i} ref={markRef} className="genius-p is-cited">
                      <span className="apr-sr-only">{(ANCHOR_NOTES[anchor] || ANCHOR_NOTES.match).spoken}</span>
                      <mark className="genius-mark">{p.text}</mark>
                    </p>
                  );
                }
                return <p key={i} className="genius-p">{p.text}</p>;
              })}
            </div>
            <p className="genius-note">
              {anchor ? ANCHOR_NOTES[anchor].note : 'The opening of this chapter.'} A short passage; the whole book is on Amazon.
            </p>
          </>
        )}

        <div className="apr-dialog-actions genius-actions">
          {data && (
            <button type="button" className="apr-btn apr-btn--quiet" onClick={() => onAsk(askTitle)}>
              Ask about this passage
            </button>
          )}
          <a className="apr-btn" href={BOOK_AMAZON_URL} target="_blank" rel="noopener">
            Get the book on Amazon
            <Icon name="external-link" size={16} />
          </a>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default GeniusReader;
